/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Select, Label, SelectOption } from 'sarsaparilla';

export default class SmartSelector extends React.Component {
    static propTypes = {
        name: PropTypes.string,
        id: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
        handleChange: PropTypes.func.isRequired,
        options: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.string,
                label: PropTypes.string,
            })
        ),
        placeholderOption: PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
        }),
        includePlaceholderOption: PropTypes.bool,
        disabled: PropTypes.bool,
        label: PropTypes.string,
        isLabelVisible: PropTypes.bool,
        hasPermission: PropTypes.bool,
        innerRef: PropTypes.shape({ current: PropTypes.node }),
        multiple: PropTypes.bool,
        isRequired: PropTypes.bool,
        validationError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
        className: PropTypes.string,
    };

    static defaultProps = {
        name: '',
        id: '',
        options: [],
        placeholderOption: {
            value: '',
            label: 'Select Option',
        },
        includePlaceholderOption: true,
        disabled: false,
        label: null,
        isLabelVisible: true,
        hasPermission: true,
        innerRef: null,
        multiple: false,
        isRequired: false,
        validationError: '',
    };

    getSelectOptions(allOptions) {
        return allOptions
            .filter((option) => option)
            .map((option) => (
                <SelectOption
                    key={option.value}
                    value={option.value}
                    isDisabled={!!option.disabled}
                >
                    {option.label}
                </SelectOption>
            ));
    }

    render() {
        const {
            options,
            name,
            id,
            value,
            handleChange,
            placeholderOption,
            includePlaceholderOption,
            disabled,
            label,
            isLabelVisible,
            innerRef,
            multiple,
            isRequired,
            validationError,
            className,
        } = this.props;

        if (!options || options.length < 1) {
            return null;
        }

        // shallow copy the options array
        const allOptions = options.slice();

        // add the placeholder option to the start of the options array
        if (includePlaceholderOption) {
            allOptions.unshift(placeholderOption);
        }

        if (multiple) {
            return (
                <div className={className}>
                    {label && (
                        <Label
                            htmlFor={id}
                            isVisible={isLabelVisible}
                            isRequired={isRequired}
                        >
                            {label}
                        </Label>
                    )}
                    <select
                        name={name}
                        id={id}
                        value={value}
                        onChange={handleChange}
                        className="rec-select"
                        multiple={multiple}
                    >
                        {allOptions
                            .filter((op) => op)
                            .map((option) => (
                                <option
                                    key={option.value}
                                    value={option.value}
                                    disabled={!!option.disabled}
                                >
                                    {option.label}
                                </option>
                            ))}
                    </select>
                </div>
            );
        }
        const selectProps = {
            label: label || 'Select',
            isLabelVisible,
            id,
            name,
            isDisabled: disabled || !this.props.hasPermission,
            onChange: handleChange,
            value,
            isInvalid: !!validationError,
            className,
        };

        // Dont include a ref in the props if it is not passed in
        if (innerRef) {
            selectProps.innerRef = innerRef;
        }

        return (
            <Select {...selectProps} isRequired={isRequired}>
                {this.getSelectOptions(allOptions)}
            </Select>
        );
    }
}
